import React, { useState } from 'react';
import { Box, Grid, Table, TableHead, TableRow, TableCell, TableBody, Typography, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from '../../components/PageHeader';
import FilterToolbar from '../../components/FilterToolbar';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField } from '../../components/InvField';
import BatchChip from '../../components/BatchChip';
import InvItemAutocomplete from '../../components/InvItemAutocomplete';
import { getBatchTrace, getPatientBatchExposure, getBatchPatientExposure } from '../../api/dispensing';
import useItemNames from '../../hooks/useItemNames';
import { formatDateTime } from '../../utils';
import { IPD_COLORS } from '../../theme';

export default function BatchTrace() {
  const { resolveItem } = useItemNames();
  const [mode, setMode] = useState('batch');
  const [batchNumber, setBatchNumber] = useState('');
  const [itemId, setItemId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [txnId, setTxnId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function doSearch() {
    setLoading(true); setError(null); setResult(null);
    try {
      let data;
      if (mode === 'batch') {
        // API: GET /inventory/dispensing/batches/{batchNumber}/patient-exposure/
        data = await getBatchPatientExposure(batchNumber, itemId || undefined);
      } else if (mode === 'patient') {
        // API: GET /inventory/dispensing/patients/{patientId}/batch-exposure/
        data = await getPatientBatchExposure(patientId);
      } else {
        // API: GET /inventory/dispensing/transactions/{txnId}/batch-trace/
        data = await getBatchTrace(txnId);
      }
      setResult(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  return (
    <Box>
      <PageHeader eyebrow="Inventory Management" title="Batch Trace"
        description="Track batch numbers to patients, or find all batches dispensed to a patient." />

      <SectionCard title="Search Parameters">
        <FilterToolbar variant="embedded">
          <FilterToolbar.Filter
            label="Search Mode"
            width="wide"
            value={mode}
            onChange={e => { setMode(e.target.value); setResult(null); }}
            options={[
              { value: 'batch', label: 'Batch → Patients' },
              { value: 'patient', label: 'Patient → Batches' },
              { value: 'txn', label: 'Transaction Trace' },
            ]}
          />
          {mode === 'batch' && (
            <>
              <FilterToolbar.Slot width={200}>
                <InvTextField
                  label="Batch Number"
                  value={batchNumber}
                  onChange={e => setBatchNumber(e.target.value)}
                  sx={{ '& .MuiFormHelperText-root': { display: 'none' } }}
                />
              </FilterToolbar.Slot>
              <FilterToolbar.Slot width={260}>
                <InvItemAutocomplete
                  label="Item (optional)"
                  value={itemId}
                  onChange={(id) => setItemId(id || '')}
                  placeholder="Search item…"
                />
              </FilterToolbar.Slot>
            </>
          )}
          {mode === 'patient' && (
            <FilterToolbar.Slot width={280}>
              <InvTextField
                label="Patient ID (UUID)"
                value={patientId}
                onChange={e => setPatientId(e.target.value)}
                sx={{ '& .MuiFormHelperText-root': { display: 'none' } }}
              />
            </FilterToolbar.Slot>
          )}
          {mode === 'txn' && (
            <FilterToolbar.Slot width={280}>
              <InvTextField
                label="Transaction ID (UUID)"
                value={txnId}
                onChange={e => setTxnId(e.target.value)}
                sx={{ '& .MuiFormHelperText-root': { display: 'none' } }}
              />
            </FilterToolbar.Slot>
          )}
          <FilterToolbar.Spacer />
          <FilterToolbar.Actions>
            <InvButton onClick={doSearch} loading={loading} startIcon={<SearchIcon />}>
              Search
            </InvButton>
          </FilterToolbar.Actions>
        </FilterToolbar>
      </SectionCard>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} /></Box>}

      {error && <Box sx={{ mt: 2, p: 2, backgroundColor: `${IPD_COLORS.error.text}15`, borderRadius: 1.5, border: `1px solid ${IPD_COLORS.error.text}33` }}>
        <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography></Box>}

      {result && (
        <SectionCard title="Trace Results" sx={{ mt: 2.5 }}>
          {Array.isArray(result) ? (
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  {mode === 'batch' && <>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Patient ID</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Admission</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Date</TableCell>
                  </>}
                  {mode === 'patient' && <>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Date</TableCell>
                  </>}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.map((row, i) => (
                  <TableRow key={i}>
                    {mode === 'batch' && <>
                      <TableCell>{row.patient_id}</TableCell>
                      <TableCell>{row.admission_id}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell>{formatDateTime(row.dispensed_at)}</TableCell>
                    </>}
                    {mode === 'patient' && <>
                      <TableCell>{resolveItem(row)}</TableCell>
                      <TableCell><BatchChip batchNumber={row.batch_number} expiryDate={row.expiry_date} /></TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell>{formatDateTime(row.dispensed_at)}</TableCell>
                    </>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box>
              {Object.entries(result).map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', gap: 2, mb: 1 }}>
                  <Typography sx={{ color: IPD_COLORS.textSecondary, minWidth: 180, fontSize: 12 }}>{k}</Typography>
                  <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 13 }}>{String(v)}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>
      )}
    </Box>
  );
}
